import type { SxProps, Theme } from '@mui/material';
import type { Building } from '../../utils/types/building.interface';

import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Grid, Skeleton, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

import { Autocomplete } from '@theguarantors/tg-package-material-ui';

import { useAddresses } from './useAddresses';

export type AddressesAutocompleteProps = {
  value: number;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  label?: string;
  name?: string;
  color?: 'primary' | 'secondary';
  size?: 'small' | 'medium';
  showLoadingSkeleton?: boolean;
  inputSx?: SxProps<Theme>;
  onBlur?: () => void;
  onChange: (property: Building | null) => void;
  'data-testid'?: string;
};

const clientFilterOptions = createFilterOptions<Building>({
  limit: 100,
  stringify: (property: Building) => `${property.name} ${property.street} ${property.city}`,
});

const serverFilterOptions = (options: Building[]) => options;

export const AddressesAutocomplete = forwardRef(
  (
    {
      label,
      error,
      required,
      helperText,
      onBlur,
      name,
      disabled,
      value,
      onChange,
      color = 'primary',
      size,
      showLoadingSkeleton = true,
      inputSx,
      'data-testid': dataTestId = 'property-chooser',
    }: AddressesAutocompleteProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const { addresses, isLoading, isSearchLoading, isNewPropertyApi, onSearchChange } =
      useAddresses();

    const createOptionsLabel = useCallback(
      (option: Building) => {
        const isDuplicatePropertyName =
          isNewPropertyApi &&
          option.property_name?.trim().toLowerCase() === option.name.trim().toLowerCase();

        const secondaryText = isNewPropertyApi
          ? isDuplicatePropertyName
            ? ''
            : option.property_name
          : formatBuildingListValue(option);

        return (
          <Grid
            container
            alignItems="center"
            data-testid={`buildings-autocomplete-option-${option.id}`}
          >
            <Grid>
              <Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
            </Grid>
            <Grid size="grow">
              <span
                style={{
                  fontWeight: 700,
                }}
              >
                {option.name}
              </span>
              {secondaryText && (
                <Typography variant="body2" color="text.secondary">
                  {secondaryText}
                </Typography>
              )}
            </Grid>
          </Grid>
        );
      },
      [isNewPropertyApi],
    );

    const lastMatchedBuilding = useRef<Building | undefined>(undefined);

    const selectedValue = useMemo(() => {
      const found = addresses?.find((addr) => addr.id === value);
      if (found) {
        return found;
      }
      if (!value) {
        return undefined;
      }
      return isNewPropertyApi ? lastMatchedBuilding.current : undefined;
    }, [addresses, value, isNewPropertyApi]);

    useEffect(() => {
      const found = addresses?.find((addr) => addr.id === value);
      if (found) {
        lastMatchedBuilding.current = found;
      } else if (!value) {
        lastMatchedBuilding.current = undefined;
      }
    }, [addresses, value]);

    const handleInputChange = useCallback(
      (_event: React.SyntheticEvent, inputValue: string, reason: string) => {
        if (reason === 'input') {
          onSearchChange(inputValue);
        } else if (reason === 'clear') {
          onSearchChange('');
        }
      },
      [onSearchChange],
    );

    if (isLoading && showLoadingSkeleton) {
      return (
        <Skeleton
          data-testid="buildings-autocomplete-loading-skeleton"
          variant="rounded"
          height={56}
          sx={{ marginBottom: '20px' }}
        />
      );
    }

    return (
      <Autocomplete<Building, false, false, false>
        clearOnBlur={true}
        size={size}
        id={'property-chooser'}
        data-testid={dataTestId}
        options={isSearchLoading ? [] : addresses || []}
        value={selectedValue}
        filterOptions={isNewPropertyApi ? serverFilterOptions : clientFilterOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value: Building | null): void => onChange(value)}
        onInputChange={isNewPropertyApi ? handleInputChange : undefined}
        disabled={disabled}
        loading={isSearchLoading}
        loadingText={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
          </Box>
        }
        getOptionLabel={(option: Building) => {
          if (!isNewPropertyApi) {
            return `${option.name} ${option.street} ${option.city}, ${option.state} ${option.zipCode}`;
          }
          const showProperty =
            option.property_name &&
            option.property_name.trim().toLowerCase() !== option.name.trim().toLowerCase();
          return showProperty ? `${option.name} - ${option.property_name}` : option.name;
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {createOptionsLabel(option)}
          </li>
        )}
        textFieldAdditionalProps={{
          label,
          error,
          required,
          helperText,
          sx: inputSx,
          onBlur,
          name,
          value,
          ref,
          color,
        }}
      />
    );
  },
);

export const formatBuildingListValue = (option: Building) => {
  let value = '';
  if (option.street) {
    value += option.street;
  }

  if (option.city) {
    value += ` ${option.city}`;
  }

  if (option.state) {
    value += ` ${option.state}`;
  }

  if (option.zipCode) {
    value += ` ${option.zipCode}`;
  }

  return value;
};

export const formatAddressListValue = (option: Building) =>
  [option.name, option.city, option.state, option.zipCode].filter(Boolean).join(' ');
